import React, { useState, useEffect } from 'react';
import { useSplash } from '../../../contexts/SplashContext';
import TranslatableInput from '../../Common/TranslatableInput';
import TranslatableTextarea from '../../Common/TranslatableTextarea';
import LanguageSwitch from '../../Common/LanguageSwitch';
import CommonInput from '../../Common/CommonInput';
import CommonSelect from '../../Common/CommonSelect';
import CommonCheckbox from '../../Common/CommonCheckbox';

import { fetchSettings } from '../../../store/modules/customer/customerActions';
import useFormSettings from '../../../hooks/useFormSettings';
import { useHasPermission } from '../../../utils/permissionUtils';
    
const CustomerForm = ({ initialData, onSave, isLoading, onClose }) => {
    const hasCreatePermission = useHasPermission(['create customers']);
    const hasUpdatePermission = useHasPermission(['update customers']);

    const { settings, isLoadingSettings, isSettingsInitialized } = useFormSettings('customer', fetchSettings);
    const { countries, vat_groups, posting_groups, customer_types } = settings || {};
    const splashData = useSplash();
    const languages = splashData?.languages || [];
    const defaultLanguage = languages.find(lang => lang.default)?.code || languages[0]?.code || 'en';
    const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);

    const [formData, setFormData] = useState({
        customer_code: '',
        name: '',
        email: '',
        phone: '',
        password: '',
        address: {},
        building: '',
        flat: '',
        road: '',
        block: '',
        area: '',
        country: null,
        vat_number: '',
        vat_group: null,
        is_domestic: false,

        posting_group: null,
        customer_type: null,
        is_active: true
    });
    
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (initialData) {
            setFormData({
                ...initialData,
                customer_code: initialData.customer_code || '',
                name: initialData.name || {},
                email: initialData.email || '',
                phone: initialData.phone || '',
                password: '',
                address: initialData.address || {},
                building: initialData.building || '',
                flat: initialData.flat || '',
                road: initialData.road || '',
                block: initialData.block || '',
                area: initialData.area || '',
                vat_number: initialData.vat_number || '',
                customer_type: initialData.customer_type || null,
                country: initialData.country || null,
                posting_group: initialData.posting_group || null,
                vat_group: initialData.vat_group || null,
                is_domestic: initialData.is_domestic ?? false,
                is_active: initialData.is_active ?? true
            });
        }
    }, [initialData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };

    const handleSelectChange = (name, value) => {
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsSaving(true);
            await onSave(formData);
            setErrors({});
            onClose();
        } catch (error) {
            if (error.errors) {
                const newErrors = {};
                Object.keys(error.errors).forEach(key => {
                    newErrors[key] = Array.isArray(error.errors[key]) 
                        ? error.errors[key][0] 
                        : error.errors[key];
                });
                setErrors(newErrors);
            } else {
                setErrors({
                    name: error.message || 'An error occurred while saving'
                });
            }
            return;
        } finally {
            setIsSaving(false);
        }
    };

    const isCreateMode = !formData?.id;
    const hasPermission = isCreateMode ? hasCreatePermission : hasUpdatePermission;

    const isSubmitting = isLoading || isSaving || !hasPermission;
    const selectedLang = languages.find(l => l.code === selectedLanguage);

    if (isLoadingSettings || !isSettingsInitialized) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="h-100 d-flex flex-column">
            <div className="d-flex justify-content-between mb-2">
                <div className="d-flex justify-content-start">
                    <h5 className="mb-0">Create Customer</h5>
                </div>
            </div>
            <div className="flex-grow-1">

                <div className="row">
                    <div className="col-md-4">
                        <CommonInput
                            name="customer_code"
                            label="Customer Code"
                            value={formData.customer_code}
                            onChange={handleChange}
                            placeholder="Enter customer code"
                            disabled={isSubmitting}
                            errors={errors}
                        />
                    </div>
                    <div className="col-md-4">
                        <TranslatableInput
                            label="Full Name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            errors={errors}
                            required={true}
                            disabled={isSubmitting}
                            selectedLanguage={selectedLanguage}
                            selectedLang={selectedLang}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonInput
                            name="email"
                            label="Email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="Leave empty or a valid email"
                            disabled={isSubmitting}
                            errors={errors}
                            required={false}
                        />
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-4">
                        <CommonInput
                            name="phone"
                            label="Phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="Enter phone number"
                            disabled={isSubmitting}
                            errors={errors}
                            required={true}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonInput
                            name="password"
                            label="Password"
                            value={formData.password}
                            onChange={handleChange}
                            placeholder="Enter password"
                            disabled={isSubmitting}
                            errors={errors}
                            required={false}
                        />
                    </div>
                    <div className="col-md-4">
                        <TranslatableTextarea
                            label="Address"
                            name="address"
                            rows={1}
                            value={formData.address}
                            onChange={handleChange}
                            errors={errors}
                            disabled={isSubmitting}
                            selectedLanguage={selectedLanguage}
                            selectedLang={selectedLang}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4">
                        <CommonInput
                            name="building"
                            label="Building"
                            value={formData.building}
                            onChange={handleChange}
                            placeholder="Enter building"
                            disabled={isSubmitting}
                            errors={errors}
                            required={true}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonInput
                            name="flat"
                            label="Flat"
                            value={formData.flat}
                            onChange={handleChange}
                            placeholder="Enter flat"
                            disabled={isSubmitting}
                            errors={errors}
                            required={true}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonInput
                            name="road"
                            label="Road"
                            value={formData.road}
                            onChange={handleChange}
                            placeholder="Enter road"
                            disabled={isSubmitting}
                            errors={errors}
                            required={true}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4">
                        <CommonInput
                            name="block"
                            label="Block"
                            value={formData.block}
                            onChange={handleChange}
                            placeholder="Enter block"
                            disabled={isSubmitting}
                            errors={errors}
                            required={true}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonInput
                            name="area"
                            label="Area"
                            value={formData.area}
                            onChange={handleChange}
                            placeholder="Enter area"
                            disabled={isSubmitting}
                            errors={errors}
                            required={true}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonSelect
                            name="country"
                            label="Country"
                            value={formData.country}
                            onChange={(value) => handleSelectChange('country', value)}
                            options={countries}
                            isSubmitting={isSubmitting}
                            errors={errors}
                            required={true}
                            placeholder="Select country..."
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4">
                        <CommonSelect
                            name="vat_group"
                            label="VAT Group"
                            value={formData.vat_group}
                            onChange={(value) => handleSelectChange('vat_group', value)}
                            options={vat_groups}
                            isSubmitting={isSubmitting}
                            errors={errors}
                            required={true}
                            placeholder="Select VAT group..."
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonInput
                            name="vat_number"
                            label="VAT Number"
                            value={formData.vat_number}
                            onChange={handleChange}
                            placeholder="Enter VAT number"
                            disabled={isSubmitting}
                            errors={errors}
                            required={false}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonCheckbox
                            name="is_domestic"
                            label="Is Domestic?"
                            value={formData.is_domestic}
                            onChange={handleChange}
                            errors={errors}
                            disabled={isSubmitting}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4">
                        <CommonSelect
                            name="posting_group"
                            label="Customer Posting Group"
                            value={formData.posting_group}
                            onChange={(value) => handleSelectChange('posting_group', value)}
                            options={posting_groups}
                            isSubmitting={isSubmitting}
                            errors={errors}
                            required={false}
                            placeholder="Select customer posting group..."
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonSelect
                            name="customer_type"
                            label="Customer Type"
                            value={formData.customer_type}
                            onChange={(value) => handleSelectChange('customer_type', value)}
                            options={customer_types}
                            isSubmitting={isSubmitting}
                            errors={errors}
                            required={true}
                            placeholder="Select customer type..."
                        />
                    </div>
                </div>


                <div className="row">
                    <div className="col-md-12">
                        <div className="mb-3">
                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    name="is_active"
                                    checked={formData.is_active}
                                    onChange={handleChange}
                                    id="is_active"
                                    disabled={isSubmitting}
                                />
                                <label className="form-check-label user-select-none" htmlFor="is_active">
                                    Active
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {hasPermission && (
            <div className="form-footer">
                <button 
                    type="button" 
                    className="btn btn-light" 
                    onClick={onClose}
                    disabled={isSubmitting}
                >
                    Cancel
                </button>
                <button 
                    type="submit" 
                    className="btn btn-primary" 
                    disabled={isSubmitting}
                >
                    {isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Saving...
                        </>
                    ) : 'Save'}
                    </button>
                </div>
            )}
        </form>
    );
};

export default CustomerForm; 